import { useState, useRef, useCallback } from "react";
import { api } from "../App";
import { toast } from "sonner";

/**
 * AdminTaskWidget — floating "Report Admin Task" button.
 * bug_7412e0f449fb. Same affordance pattern as BugReportWidget but parallel:
 *   - Routes to POST /admin-tasks (NOT /bugs)
 *   - Captures requester + timestamp server-side
 *   - Defaults assignee to "Mukesh" (until he's onboarded)
 *
 * Positioned offset from the bug widget so the two FABs don't overlap on
 * default placement. Both are independently draggable.
 */
export default function AdminTaskWidget() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [submitting, setSubmitting] = useState(false);

  // Default position: offset to the LEFT of the bug widget so they don't
  // collide. Bug widget defaults to right=24, this defaults to right=180.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [pos, setPos] = useState({
    bottom: typeof window !== "undefined" && window.innerWidth < 768 ? 64 : 24,
    right: 200,
  });
  const dragRef = useRef({
    dragging: false, startX: 0, startY: 0, startRight: 200, startBottom: 24, moved: false,
  });

  const onMouseDown = useCallback((e) => {
    if (open) return;
    e.preventDefault();
    dragRef.current = {
      dragging: true, startX: e.clientX, startY: e.clientY,
      startRight: pos.right, startBottom: pos.bottom, moved: false,
    };
    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
      setPos({
        right: Math.max(8, Math.min(window.innerWidth - 200, dragRef.current.startRight - dx)),
        bottom: Math.max(8, Math.min(window.innerHeight - 50, dragRef.current.startBottom - dy)),
      });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!dragRef.current.moved) setOpen((o) => !o);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [open, pos]);

  const onTouchStart = useCallback((e) => {
    if (open) return;
    const t = e.touches[0];
    dragRef.current = {
      dragging: true, startX: t.clientX, startY: t.clientY,
      startRight: pos.right, startBottom: pos.bottom, moved: false,
    };
  }, [open, pos]);
  const onTouchMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const t = e.touches[0];
    const dx = t.clientX - dragRef.current.startX;
    const dy = t.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    setPos({
      right: Math.max(8, Math.min(window.innerWidth - 50, dragRef.current.startRight - dx)),
      bottom: Math.max(70, Math.min(window.innerHeight - 50, dragRef.current.startBottom - dy)),
    });
  }, []);
  const onTouchEnd = useCallback(() => {
    dragRef.current.dragging = false;
    if (!dragRef.current.moved) setOpen((o) => !o);
  }, []);

  const submit = async () => {
    const t = title.trim();
    if (!t) { toast.error("Title is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin-tasks", {
        title: t,
        description: description.trim(),
        priority,
      });
      toast.success("Admin request filed — Mukesh notified");
      setTitle(""); setDescription(""); setPriority("Medium"); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't file request");
    }
    setSubmitting(false);
  };

  // Hide when not signed in — same gate as the bug widget
  if (typeof window === "undefined" || !localStorage.getItem("dreamteam_user")) return null;
  if (isMobile) return null; // Hide on mobile — keep IG-style chrome clean

  // Brand color: amber/orange — distinct from the red bug FAB and the
  // blue/green action accents elsewhere. Reads as "ops / admin work".
  const brand = "rgba(245,158,11,0.92)";
  const brandSolid = "#f59e0b";

  return (
    <>
      <button
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        title="Report an admin task (drag to move)"
        style={{
          position: "fixed",
          bottom: `${pos.bottom}px`,
          right: `${pos.right}px`,
          zIndex: 9999,
          padding: "10px 18px",
          height: "42px",
          borderRadius: "24px",
          background: brand,
          color: "white",
          border: "none",
          cursor: "grab",
          fontSize: "13px",
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          letterSpacing: ".05em",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          transition: "none",
          userSelect: "none",
        }}
      >
        📋 ADMIN TASK
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            bottom: `${pos.bottom + 62}px`,
            right: `${pos.right}px`,
            zIndex: 9999,
            width: "360px",
            background: "var(--s2)",
            border: "1px solid var(--bdr)",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontFamily: "var(--font-orb)", fontSize: "14px", color: "var(--t1)", margin: 0 }}>
              Report an Admin Task
            </h3>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none", border: "none", color: "var(--t3)",
                cursor: "pointer", fontSize: "18px", padding: "2px 6px",
                borderRadius: "4px", lineHeight: 1,
              }}
              title="Close"
            >
              ✕
            </button>
          </div>

          <p style={{ fontSize: "11px", color: "var(--t3)", margin: "0 0 10px 0", lineHeight: 1.4 }}>
            Files a request for the admin manager. Captures who and when
            automatically — resolution timestamp gets stamped on close.
          </p>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What do you need? *"
            autoFocus
            style={{
              width: "100%", padding: "8px 12px", marginBottom: "8px",
              background: "var(--s1)", border: "1px solid var(--bdr)",
              borderRadius: "8px", color: "var(--t1)", fontSize: "13px",
              fontFamily: "var(--font-mono)", boxSizing: "border-box",
            }}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add context (optional) — vendor, deadline, why it matters…"
            rows={3}
            style={{
              width: "100%", padding: "8px 12px", marginBottom: "8px",
              background: "var(--s1)", border: "1px solid var(--bdr)",
              borderRadius: "8px", color: "var(--t1)", fontSize: "13px",
              fontFamily: "var(--font-mono)", resize: "vertical", boxSizing: "border-box",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", color: "var(--t3)" }}>Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{
                padding: "5px 8px", background: "var(--s1)",
                border: "1px solid var(--bdr)", borderRadius: "6px",
                color: "var(--t1)", fontSize: "12px", fontFamily: "var(--font-mono)",
              }}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
            <span style={{ fontSize: "11px", color: "var(--t3)", marginLeft: "auto" }}>→ Mukesh</span>
          </div>

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                padding: "6px 14px", background: "transparent",
                color: "var(--t2)", border: "1px solid var(--bdr)",
                borderRadius: "8px", cursor: "pointer", fontSize: "12px",
              }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              style={{
                padding: "6px 14px",
                background: brandSolid, color: "white",
                border: "none", borderRadius: "8px",
                cursor: "pointer", fontSize: "12px", fontWeight: 600,
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Filing…" : "File request"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
